import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAvailableBrands, getCurrentBrand, switchBrand } from '../services/brandService';
import { getCurrentUser } from '../services/auth';

const BrandContext = createContext();

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

export const BrandProvider = ({ children }) => {
  const [currentBrand, setCurrentBrand] = useState(null);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status and load brands only if authenticated
  useEffect(() => {
    const user = getCurrentUser();
    console.log('[BrandContext] Initializing - User from localStorage:', user);
    
    if (user) {
      console.log('[BrandContext] User authenticated, loading brands...');
      setIsAuthenticated(true);
      loadBrands();
    } else {
      console.log('[BrandContext] No authenticated user found');
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, []);

  const loadBrands = async () => {
    // Only load if authenticated
    const user = getCurrentUser();
    if (!user) {
      console.log('[BrandContext] No authenticated user - skipping brand load');
      setAvailableBrands([]);
      setCurrentBrand(null);
      setLoading(false);
      return;
    }

    // Prevent duplicate loading
    if (loading) {
      console.log('[BrandContext] Already loading brands, skipping duplicate request');
      return;
    }

    try {
      console.log('[BrandContext] Starting to fetch brands from API...');
      setLoading(true);
      setError(null);
      
      // Fetch available brands and current brand in parallel
      const [brands, current] = await Promise.all([
        getAvailableBrands(),
        getCurrentBrand()
      ]);
      
      console.log('[BrandContext] API Response - Available brands:', brands);
      console.log('[BrandContext] API Response - Current brand:', current);
      
      setAvailableBrands(brands || []);
      setCurrentBrand(current || null);
      
      console.log('[BrandContext] Brands loaded successfully:', {
        brandsCount: brands?.length,
        currentBrand: current?.name,
        hasMultipleBrands: (brands?.length || 0) > 1
      });
    } catch (err) {
      console.error('[BrandContext] Error loading brands:', err);
      
      // Don't set error for authentication issues - user is simply not logged in
      if (err.response && err.response.status === 401) {
        console.log('[BrandContext] Not authenticated - clearing brand state');
        setAvailableBrands([]);
        setCurrentBrand(null);
      } else {
        console.error('[BrandContext] Failed to load brands:', err);
        setError(err.message || 'Failed to load brand information');
        setAvailableBrands([]);
        setCurrentBrand(null);
      }
    } finally {
      setLoading(false);
      console.log('[BrandContext] Loading complete');
    }
  };

  // Method to initialize brands after login
  const initializeBrands = async () => {
    setIsAuthenticated(true);
    await loadBrands();
  };

  // Method to clear brands on logout
  const clearBrands = () => {
    setIsAuthenticated(false);
    setCurrentBrand(null);
    setAvailableBrands([]);
    setLoading(false);
    setError(null);
  };

  const handleSwitchBrand = async (brandId) => {
    if (switching) return; // Prevent multiple simultaneous switches
    
    try {
      setSwitching(true);
      setError(null);
      
      // Call the API to switch brands
      const newBrand = await switchBrand(brandId);
      
      // Update current brand
      setCurrentBrand(newBrand);
      
      // Reload the page to refresh all data with new brand context
      // This ensures all components are properly reset with the new brand
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch brand:', err);
      setError(err.message || 'Failed to switch brand');
      setSwitching(false);
    }
  };

  const refreshBrands = async () => {
    await loadBrands();
  };

  const value = {
    currentBrand,
    availableBrands,
    loading,
    switching,
    error,
    isAuthenticated,
    switchBrand: handleSwitchBrand,
    refreshBrands,
    initializeBrands,
    clearBrands,
    // Helper to check if user has multiple brands
    hasMultipleBrands: availableBrands.length > 1
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};

export default BrandContext;

